import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useToast } from '../../components/ds';
import { Button, Table, Badge, Spinner, Icon } from '../../components/ds';
import PlacementTypeModal from './PlacementTypeModal';
import FieldConfigModal from './FieldConfigModal';
import './TemplatesPage.css';


export default function TemplatesPage() {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [placementTypes, setPlacementTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [fieldConfigTemplate, setFieldConfigTemplate] = useState(null);
  const [refreshingUuids, setRefreshingUuids] = useState(new Set());
  const [needsSyncUuids, setNeedsSyncUuids] = useState(new Set());
  const [syncingAll, setSyncingAll] = useState(false);
  const { addToast } = useToast();

  const fetchData = useCallback(async () => {
    try {
      const [tpls, types] = await Promise.all([
        api.getTemplates(),
        api.getPlacementTypes(),
      ]);
      setTemplates(tpls || []);
      setPlacementTypes(types || []);
    } catch (err) {
      addToast('載入資料失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const checkUpdates = useCallback(async () => {
    try {
      const res = await api.checkTemplateUpdates();
      const uuids = new Set((res.needs_sync || []).map((t) => t.uuid));
      setNeedsSyncUuids(uuids);
    } catch {
      // non-blocking — silently ignore
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Fire check-updates in parallel without blocking list render
    checkUpdates();
  }, [fetchData, checkUpdates]);

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await api.syncAllTemplates();
      const synced = res.synced || [];
      const totalAdded = synced.reduce((sum, t) => sum + (t.added || []).length, 0);
      const totalObsolete = synced.reduce((sum, t) => sum + (t.obsolete || []).length, 0);
      addToast(
        `全部同步完成：${synced.length} 個 Template，新增 ${totalAdded} 個欄位`,
        'success'
      );
      if (totalObsolete > 0) {
        addToast(`${totalObsolete} 個欄位已標記為廢棄（未刪除），請至「設定參數」確認`, 'info');
      }
      setNeedsSyncUuids(new Set());
      fetchData();
    } catch (err) {
      addToast('全部同步失敗：' + err.message, 'error');
    } finally {
      setSyncingAll(false);
    }
  };

  const handlePlacementChange = async (template, typeId) => {
    try {
      const res = await api.updateTemplate(template.uuid, {
        template_name: template.name,
        placement_type_id: typeId || null,
      });
      if (typeId && res && res.warning === 'template_meta_unavailable') {
        addToast('Template 已啟用，但樣板參數需要進入「設定參數」儲存才能正常使用 Portal Creative Editor', 'info');
      } else {
        addToast(typeId ? 'Template 已啟用' : 'Template 已停用', 'success');
      }
      fetchData();
    } catch (err) {
      addToast('更新失敗：' + err.message);
    }
  };

  const handleDeleteType = async (id, name) => {
    if (!window.confirm(`確定要刪除「${name}」？使用此類型的 Template 將會無法使用。`)) return;
    try {
      await api.deletePlacementType(id);
      addToast('已刪除', 'success');
      fetchData();
    } catch (err) {
      addToast('刪除失敗：' + err.message);
    }
  };

  const handleTypeModalSave = () => {
    setShowTypeModal(false);
    setEditingType(null);
    fetchData();
  };

  const handleRefreshMeta = async (uuid) => {
    setRefreshingUuids((prev) => new Set(prev).add(uuid));
    try {
      const res = await api.refreshTemplateMeta(uuid);
      if (res.warning === 'template_meta_unavailable') {
        addToast('PAC 暫時無法取得，請稍後再試', 'info');
      } else {
        const added = (res.added || []).length;
        const removed = (res.removed || []).length;
        if (added > 0 || removed > 0) {
          addToast(
            `同步完成：新增 ${added} 個欄位（已預設隱藏）/ 移除 ${removed} 個欄位 — 請至『設定參數』確認`,
            'success'
          );
        } else {
          addToast('樣板無變更', 'info');
        }
        setNeedsSyncUuids((prev) => { const next = new Set(prev); next.delete(uuid); return next; });
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setRefreshingUuids((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }
  };

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 className="h1 h1--lg">Template 管理</h1>
      </div>

      {/* Tabs - 暫時保留自建，Phase 2 會提供 <Tabs> 元件 */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Template 清單
        </button>
        <button
          className={`tab ${activeTab === 'types' ? 'active' : ''}`}
          onClick={() => setActiveTab('types')}
        >
          Placement Type 管理
        </button>
      </div>

      {activeTab === 'templates' && needsSyncUuids.size > 0 && (
        <div className="sync-prompt">
          <span className="sync-prompt__text">
            <Badge variant="warning">{needsSyncUuids.size}</Badge>
            &nbsp;個 template 有更新
          </span>
          <Button
            size="sm"
            onClick={handleSyncAll}
            disabled={syncingAll}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {syncingAll ? <Spinner style={{ width: 14, height: 14, borderWidth: 2 }} /> : <Icon name="RefreshCw" size={14} />}
            全部同步
          </Button>
        </div>
      )}

      {activeTab === 'templates' && (
        <Table
          columns={[
            { key: 'name', title: 'Template 名稱', render: (val, row) => (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {val}
                {needsSyncUuids.has(row.uuid) && <Badge variant="warning">有更新</Badge>}
              </span>
            )},
            { key: 'placement_type_id', title: '狀態', render: (val) => (
              <Badge variant={val != null ? 'success' : 'default'}>{val != null ? '可用' : '未設定'}</Badge>
            )},
            { key: 'placement_type_id', title: 'Placement Type', render: (val, row) => (
              <select
                value={val || ''}
                onChange={(e) => handlePlacementChange(row, e.target.value ? parseInt(e.target.value) : null)}
                style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}
              >
                <option value="">不可用</option>
                {placementTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.display_name || pt.name}
                  </option>
                ))}
              </select>
            )},
            { key: 'actions', title: '操作', render: (_, row) => (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!row.placement_type_id}
                  title={row.placement_type_id ? '設定欄位顯示/分類' : '請先選擇 Placement Type'}
                  onClick={() => row.placement_type_id && setFieldConfigTemplate(row)}
                >
                  設定參數
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={refreshingUuids.has(row.uuid)}
                  title="從 PAC 重新同步欄位（新欄位預設隱藏）"
                  onClick={() => handleRefreshMeta(row.uuid)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {refreshingUuids.has(row.uuid) ? <Spinner style={{ width: 14, height: 14, borderWidth: 2 }} /> : <Icon name="RefreshCw" size={14} />}
                  同步
                </Button>
              </div>
            )},
          ]}
          data={templates}
          rowKey="uuid"
          emptyText="找不到 Template"
        />
      )}

      {activeTab === 'types' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Button onClick={() => setShowTypeModal(true)}>+ 新增 Placement Type</Button>
          </div>
          <Table
            columns={[
              { key: 'name', title: '名稱', render: (val) => <code>{val}</code> },
              { key: 'display_name', title: '顯示名稱', render: (val) => val || '-' },
              { key: 'platform', title: '平台', render: (val) => val || '-' },
              { key: 'template_count', title: 'Template 數量' },
              { key: 'actions', title: '操作', render: (_, row) => (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" size="sm" onClick={() => { setEditingType(row); setShowTypeModal(true); }}>
                    編輯
                  </Button>
                  <Button variant="secondary" size="sm" style={{ color: 'var(--status-error-text)' }} onClick={() => handleDeleteType(row.id, row.name)}>
                    刪除
                  </Button>
                </div>
              )},
            ]}
            data={placementTypes}
            rowKey="id"
            emptyText="尚無 Placement Type"
          />
        </div>
      )}

      {fieldConfigTemplate && (
        <FieldConfigModal
          template={fieldConfigTemplate}
          onClose={() => setFieldConfigTemplate(null)}
        />
      )}

      {showTypeModal && (
        <PlacementTypeModal
          type={editingType}
          onClose={() => { setShowTypeModal(false); setEditingType(null); }}
          onSaved={handleTypeModalSave}
        />
      )}
    </div>
  );
}
